import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ThreadForm from '../components/ThreadForm'
import { ForumThread } from '../types'

interface ThreadFormData {
  title: string
  content: string
  categoryId: string
  tags: string[]
  isPinned?: boolean
}

export default function CreateThread() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user, isLoggedIn } = useAuth()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const categoryId = searchParams.get('category') || undefined

  if (!isLoggedIn) {
    return (
      <div className="create-thread-page">
        <div className="container">
          <div className="auth-required">
            <h1>Login Required</h1>
            <p>You must be logged in to create new threads.</p>
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary"
            >
              Login
            </button>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (threadData: ThreadFormData) => {
    if (!user) return

    setIsSubmitting(true)
    setError(null)

    try {
      // Create the thread object
      const newThread: Partial<ForumThread> = {
        id: `thread_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        categoryId: threadData.categoryId,
        title: threadData.title,
        content: threadData.content,
        authorId: user.id,
        authorUsername: user.username,
        authorRole: user.role,
        dateCreated: new Date(),
        lastActivity: new Date(),
        isPinned: threadData.isPinned || false,
        isLocked: false,
        isDeleted: false,
        views: 0,
        postCount: 0,
        upvotes: 0,
        downvotes: 0,
        tags: threadData.tags
      }

      // TODO: Replace with actual API call
      console.log('Creating thread:', newThread)
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000))

      // TODO: Store thread in backend/database
      // For now, we'll just simulate success

      // Navigate to the new thread
      navigate(`/community/thread/${newThread.id}`)
      
    } catch (err) {
      setError('Failed to create thread. Please try again.')
      console.error('Error creating thread:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/community')
  }

  return (
    <div className="create-thread-page">
      <div className="container">
        <div className="page-header">
          <nav className="breadcrumb">
            <a href="/community">Community</a>
            <span>→</span>
            <span>Create Thread</span>
          </nav>
        </div>

        {error && (
          <div className="error-banner">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
            <button 
              onClick={() => setError(null)}
              className="close-error"
              aria-label="Close error"
            >
              ×
            </button>
          </div>
        )}

        <ThreadForm
          categoryId={categoryId}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  )
}